import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { SOURCE_INDEX, getSourceIndexTotals } from '../data/sourceIndex';
import LastReviewedStamp from '../components/seo/LastReviewedStamp';
import { BreadcrumbsContainer } from '../components/seo/Breadcrumbs';
import BuildPilotOpinion from '../components/editorial/BuildPilotOpinion';
import useJsonLd from '../hooks/useJsonLd';

/**
 * SourceIndexPage, the BuildPilot Source Index at /sources.
 *
 * What it is:
 *   A single page that maps every external regulatory and primary-source
 *   citation used across BuildPilot content, grouped by issuing authority
 *   (federal, state, industry bodies, standards). Each entry shows what
 *   we cite the source for and links to the BuildPilot pages where the
 *   citation appears.
 *
 * Why it exists:
 *   1. E-E-A-T transparency. Google's Search Quality rater guidelines
 *      treat sites that publish a discoverable "we cite these
 *      authorities" page as more trustworthy than sites that only embed
 *      citations inline.
 *   2. AI Overview citation magnet. ChatGPT, Perplexity, Claude and
 *      Gemini grounding crawls prefer one stable URL that maps the
 *      editorial chain back to authoritative sources.
 *   3. Copyright posture. Makes explicit that BuildPilot quotes
 *      regulations and government data, and contributes independent
 *      editorial interpretation on top.
 *
 * Data source: /data/sourceIndex.js
 * Sibling page: /data/seoInventory.js (which powers /sitemap)
 */
const SourceIndexPage = () => {
  const { totalSources, totalGroups, totalCitedTopics, totalInternalLinks } = getSourceIndexTotals();
  const groups = Object.entries(SOURCE_INDEX);

  // CollectionPage + ItemList schema, modelled to make each authority an
  // Organization node so AI crawlers (and Google Knowledge Graph) can
  // link the BuildPilot entity into the wider construction-industry graph.
  useJsonLd(
    [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': 'https://buildpilot.com.au/sources',
        url: 'https://buildpilot.com.au/sources',
        name: `BuildPilot Source Index, ${totalSources} authorities cited across our content`,
        description: `The complete list of regulatory and primary-source authorities BuildPilot cites across our editorial content. ${totalSources} sources grouped into ${totalGroups} categories, covering ${totalCitedTopics} cited topics with ${totalInternalLinks} cross-links to where each citation appears on BuildPilot.`,
        isPartOf: { '@id': 'https://buildpilot.com.au#website' },
        dateModified: '2026-03-01',
        inLanguage: 'en-AU',
        author: { '@id': 'https://buildpilot.com.au/#editorial' },
        publisher: { '@id': 'https://buildpilot.com.au/#organization' },
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: totalSources,
          itemListElement: groups.flatMap(([groupName, items], gIdx) =>
            items.map((src, idx) => ({
              '@type': 'ListItem',
              position: gIdx * 100 + idx + 1,
              item: {
                '@type': 'Organization',
                name: src.name,
                url: src.url,
                description: src.summary,
              },
            }))
          ),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://buildpilot.com.au' },
          { '@type': 'ListItem', position: 2, name: 'Source Index', item: 'https://buildpilot.com.au/sources' },
        ],
      },
    ],
    'source-index-page'
  );

  return (
    <div className="min-h-screen bg-white" data-testid="source-index-page">
      <Helmet>
        <title>BuildPilot Source Index, the authorities we cite (Adelaide, 2026)</title>
        <meta
          name="description"
          content={`The ${totalSources} regulatory and primary-source authorities cited across BuildPilot's editorial content, covering ${totalCitedTopics} topics. Federal, South Australian Government, industry bodies and standards, all in one place, refreshed quarterly.`}
        />
        <meta name="robots" content="index, follow, max-snippet:-1" />
        <link rel="canonical" href="https://buildpilot.com.au/sources" />
        <meta property="og:title" content="BuildPilot Source Index, the authorities we cite" />
        <meta property="og:description" content="Every regulatory and primary-source citation BuildPilot uses, grouped by authority, refreshed quarterly." />
        <meta property="og:url" content="https://buildpilot.com.au/sources" />
        <meta property="og:type" content="website" />
      </Helmet>

      <BreadcrumbsContainer items={[{ name: 'Source Index' }]} variant="light" />

      <header className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-8 sm:pb-10">
        <h1 className="text-[32px] sm:text-[44px] lg:text-[54px] font-bold text-[#1d1d1f] leading-[1.05] tracking-tight max-w-3xl">
          The authorities we cite, in one place.
        </h1>
        <p className="text-[15px] sm:text-[17px] text-[#5a5a5f] mt-4 sm:mt-5 max-w-2xl leading-relaxed">
          BuildPilot is an independent editorial layer above the Adelaide building industry, not part of it. Every cost figure, grant threshold, regulatory rule and construction standard we quote points back to one of the {totalSources} authorities listed below. We refresh this page each quarter as legislation, grants and standards change.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px] text-gray-500">
          <LastReviewedStamp date="2026-03-01" className="!text-gray-500" />
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span>{totalSources} authorities</span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span>{totalCitedTopics} cited topics</span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span>{totalInternalLinks} cross-links into BuildPilot content</span>
        </div>
      </header>

      {/* How we use this index, editorial framing */}
      <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="bg-[#f5f7ff] border border-[#dce5ff] rounded-2xl p-5 sm:p-7" data-testid="source-index-framing">
          <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[#1d8efe] mb-2">
            How we use these sources
          </p>
          <p className="text-[14.5px] sm:text-[15.5px] text-[#1d1d1f] leading-relaxed">
            Facts and figures (grant amounts, stamp duty thresholds, NCC standards, BAL ratings, structural warranty periods) come from the authoritative public sources listed below and are not BuildPilot&rsquo;s own data. Editorial interpretation (which builder suits which budget band, how to read a contract, what a &ldquo;good&rdquo; Adelaide block looks like) is BuildPilot&rsquo;s own work, grounded in the personal qualifications of BuildPilot&rsquo;s founder, <Link to="/about/editorial-team" className="text-[#1d8efe] hover:underline font-medium">George Giannakakis</Link>: a Bachelor of Architectural Studies and a Master of Architecture (University of South Australia), and an active SA real estate land agent licence (RLA300580). Each card below carries a &ldquo;BuildPilot opinion&rdquo; callout; that is George&rsquo;s editorial reading based on his work with Adelaide families and is not legal, tax, financial or planning advice. For real, accountable advice on your specific build, use <Link to="/one-on-one-support" className="text-[#1d8efe] hover:underline font-medium">One on One Support</Link>. Where we cite a primary source, we link it. Where we offer an opinion, we say so.
          </p>
        </div>
      </section>

      <main className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        {groups.map(([groupName, items]) => (
          <section key={groupName} className="mb-12 sm:mb-14" data-testid={`source-group-${groupName.toLowerCase().replace(/\s+/g, '-')}`}>
            <h2 className="text-[20px] sm:text-[26px] font-bold text-[#1d1d1f] leading-tight mb-6 pb-3 border-b border-gray-200">
              {groupName}
              <span className="ml-3 text-[14px] sm:text-[16px] text-gray-400 font-normal">
                ({items.length})
              </span>
            </h2>

            <div className="space-y-5 sm:space-y-6">
              {items.map((src) => (
                <article
                  key={src.name}
                  className="bg-white border border-gray-200 hover:border-[#1d8efe] rounded-xl p-5 sm:p-6 transition-colors"
                  data-testid={`source-${src.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 50)}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <h3 className="text-[15px] sm:text-[17px] font-semibold text-[#1d1d1f] leading-snug">
                      <a
                        href={src.url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="hover:text-[#1d8efe] underline-offset-2 hover:underline inline-flex items-center gap-1.5"
                      >
                        {src.name}
                        <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                      </a>
                    </h3>
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-gray-400 mt-1">
                      {src.jurisdiction}
                    </span>
                  </div>

                  <p className="text-[13.5px] sm:text-[14.5px] text-[#5a5a5f] leading-relaxed mb-4">
                    {src.summary}
                  </p>

                  {src.citedFor && src.citedFor.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-gray-500 mb-2">
                        We cite this source for
                      </p>
                      <ul className="space-y-1">
                        {src.citedFor.map((topic, i) => (
                          <li key={i} className="text-[13px] text-[#3a3a3f] leading-snug pl-3 relative">
                            <span className="absolute left-0 top-2 w-1 h-1 rounded-full bg-gray-300" />
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {src.internalLinks && src.internalLinks.length > 0 && (
                    <div>
                      <p className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-gray-500 mb-2">
                        Where on BuildPilot
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {src.internalLinks.map((l) => (
                          <Link
                            key={l.href}
                            to={l.href}
                            className="inline-flex items-center px-2.5 py-1 bg-[#f3f4f6] hover:bg-[#1d8efe] hover:text-white border border-gray-200 hover:border-[#1d8efe] rounded-full text-[11.5px] text-gray-700 transition-colors"
                            data-testid={`internal-link-${l.href.replace(/[^a-z0-9]+/gi, '-').slice(0, 40)}`}
                          >
                            {l.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* BuildPilot's opinion on this authority. Clearly framed
                      as opinion ("our reading", "in our experience") rather
                      than advice. Uses the canonical BuildPilotOpinion
                      component (brand-blue gradient) so styling stays
                      consistent sitewide and never drifts back to pastel. */}
                  {src.buildpilotTake && (
                    <BuildPilotOpinion className="mt-4">
                      {src.buildpilotTake}
                    </BuildPilotOpinion>
                  )}

                  <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-400">
                    Verified <time dateTime={src.lastVerified}>{new Date(src.lastVerified).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</time>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}

        {/* Closing editorial block */}
        <aside className="mt-10 p-6 sm:p-8 bg-gray-50 rounded-2xl border border-gray-100" data-testid="source-index-closing">
          <h2 className="text-[16px] sm:text-[18px] font-semibold text-[#1d1d1f] mb-2">
            Spotted an out-of-date citation?
          </h2>
          <p className="text-[13.5px] sm:text-[14.5px] text-[#5a5a5f] leading-relaxed">
            Legislation, grants and codes change. We refresh this page each quarter, but if you spot a stale citation or a regulatory update we have missed, let us know at{' '}
            <a href="mailto:editorial@buildpilot.com.au" className="text-[#1d8efe] hover:underline font-medium">
              editorial@buildpilot.com.au
            </a>{' '}
            and we will update the index. To read every BuildPilot editorial opinion in one place, visit the <Link to="/editorial-notebook" className="text-[#1d8efe] hover:underline font-medium">Editorial Notebook</Link>. For machine-readable access to BuildPilot&rsquo;s own data, AI grounding crawls should use{' '}
            <a href="/llms.txt" className="text-[#1d8efe] hover:underline font-medium">/llms.txt</a>{' '}
            (which references this index).
          </p>
        </aside>
      </main>
    </div>
  );
};

export default SourceIndexPage;
